/**
 * Generic search-first combobox for entity pickers (units, parties).
 *
 * Why custom and not cmdk/shadcn: no shadcn primitives are wired up in
 * this repo yet, and we only need ~80 lines of behaviour — text input
 * with debounced `onSearch`, dropdown listbox, click to pick. Add proper
 * keyboard navigation when a second consumer asks for it.
 */

import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';

export type EntityComboboxOption<T> = {
  id: string;
  primary: string;
  secondary?: string | null;
  value: T;
};

export function EntityCombobox<T>({
  placeholder,
  onSearch,
  onPick,
  emptyHint,
  autoFocus,
  defaultOpen,
}: {
  placeholder: string;
  onSearch: (q: string) => Promise<EntityComboboxOption<T>[]>;
  onPick: (opt: EntityComboboxOption<T>) => void;
  emptyHint?: string;
  /** Focus the input on mount — used when the combobox is the primary
   *  affordance of a card (e.g. the self-party picker on a lessor slot). */
  autoFocus?: boolean;
  /** Show the results dropdown immediately, before the input is focused,
   *  so an empty query surfaces its zero-query results (self-parties). */
  defaultOpen?: boolean;
}) {
  const [q, setQ] = useState('');
  const [options, setOptions] = useState<EntityComboboxOption<T>[] | null>(null);
  const [open, setOpen] = useState(!!defaultOpen);
  const [loading, setLoading] = useState(false);
  const debounce = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true);
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(async () => {
      try {
        const r = await onSearch(q);
        setOptions(r);
      } finally {
        setLoading(false);
      }
    }, 150);
    return () => {
      if (debounce.current) window.clearTimeout(debounce.current);
    };
  }, [q, onSearch]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2 rounded border border-stone-300 bg-white px-2 py-1.5">
        <Search className="h-3.5 w-3.5 text-stone-400" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-stone-400"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ('')}
            className="text-stone-400 hover:text-stone-600"
            aria-label="Rensa"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded border border-stone-200 bg-white shadow-lg">
          {loading && options === null ? (
            <div className="p-3 text-xs text-stone-400">Söker…</div>
          ) : !options || options.length === 0 ? (
            <div className="p-3 text-xs text-stone-500">{emptyHint ?? 'Inga träffar.'}</div>
          ) : (
            <ul>
              {options.map((opt) => (
                <li key={opt.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onPick(opt);
                      setOpen(false);
                      setQ('');
                    }}
                    className="block w-full px-3 py-2 text-left hover:bg-stone-100"
                  >
                    <div className="text-sm font-medium">{opt.primary}</div>
                    {opt.secondary && (
                      <div className="text-xs text-stone-500">{opt.secondary}</div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
